import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  PlayCircle, Pause, FileText, HelpCircle, CheckCircle, 
  ChevronDown, ChevronRight, Clock, BookOpen, Award,
  ArrowLeft, Download, RefreshCw, Lock, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// YouTube Video Player Component
const YouTubePlayer = ({ videoUrl, onProgress, onComplete, lastPosition }) => {
  const [player, setPlayer] = useState(null);
  const playerRef = useRef(null);
  const progressInterval = useRef(null);

  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(videoUrl);

  useEffect(() => {
    if (!videoId) return;

    // Load YouTube IFrame API
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    window.onYouTubeIframeAPIReady = () => {
      const newPlayer = new window.YT.Player(playerRef.current, {
        videoId,
        playerVars: {
          autoplay: 0,
          modestbranding: 1,
          rel: 0,
          start: lastPosition || 0
        },
        events: {
          onReady: (event) => {
            setPlayer(event.target);
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              // Track progress every 10 seconds
              progressInterval.current = setInterval(() => {
                const currentTime = event.target.getCurrentTime();
                const duration = event.target.getDuration();
                const percent = (currentTime / duration) * 100;
                onProgress?.(percent, Math.floor(currentTime));
              }, 10000);
            } else {
              clearInterval(progressInterval.current);
            }
            if (event.data === window.YT.PlayerState.ENDED) {
              onComplete?.();
            }
          }
        }
      });
    };

    // If API already loaded
    if (window.YT && window.YT.Player) {
      window.onYouTubeIframeAPIReady();
    }

    return () => {
      clearInterval(progressInterval.current);
    };
  }, [videoId]);

  if (!videoId) {
    return (
      <div className="aspect-video bg-slate-900 flex items-center justify-center text-white">
        <p>Invalid video URL</p>
      </div>
    );
  }

  return (
    <div className="aspect-video bg-black">
      <div ref={playerRef} className="w-full h-full" />
    </div>
  );
};

// Direct Video Player Component
const DirectVideoPlayer = ({ videoUrl, onProgress, onComplete, lastPosition }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current && lastPosition) {
      videoRef.current.currentTime = lastPosition;
    }
  }, [lastPosition]);

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const percent = (videoRef.current.currentTime / videoRef.current.duration) * 100;
    onProgress?.(percent, Math.floor(videoRef.current.currentTime));
  };

  return (
    <video
      ref={videoRef}
      src={videoUrl}
      controls
      className="w-full aspect-video bg-black"
      onTimeUpdate={handleTimeUpdate}
      onEnded={onComplete}
    />
  );
};

// Quiz Component
const QuizComponent = ({ quiz, onSubmit, existingScore }) => {
  const [answers, setAnswers] = useState(Array(quiz.length).fill(-1));
  const [submitted, setSubmitted] = useState(!!existingScore);
  const [result, setResult] = useState(existingScore ? { score: existingScore, passed: existingScore >= 60 } : null);

  const handleSubmit = async () => {
    const res = await onSubmit(answers);
    setResult(res);
    setSubmitted(true);
  };

  const resetQuiz = () => {
    setAnswers(Array(quiz.length).fill(-1));
    setSubmitted(false);
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
        <HelpCircle className="h-5 w-5 text-amber-600" /> Lesson Quiz
      </h3>

      {result && (
        <div className={`p-4 rounded-lg ${result.passed ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-bold ${result.passed ? 'text-emerald-700' : 'text-red-700'}`}>
                {result.passed ? 'Congratulations! You Passed!' : 'Try Again!'}
              </p>
              <p className="text-sm text-slate-600">Score: {result.score?.toFixed(1)}%</p>
            </div>
            {!result.passed && (
              <Button variant="outline" onClick={resetQuiz}>Retry Quiz</Button>
            )}
          </div>
        </div>
      )}

      {!submitted && (
        <>
          {quiz.map((q, qIdx) => (
            <div key={qIdx} className="bg-white border rounded-lg p-4">
              <p className="font-medium text-slate-800 mb-3">
                {qIdx + 1}. {q.question}
                <span className="text-xs text-slate-400 ml-2">({q.points} points)</span>
              </p>
              <div className="space-y-2">
                {q.options.map((opt, oIdx) => (
                  <label key={oIdx} className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-slate-50 ${answers[qIdx] === oIdx ? 'bg-blue-50 border border-blue-200' : ''}`}>
                    <input
                      type="radio"
                      name={`q_${qIdx}`}
                      checked={answers[qIdx] === oIdx}
                      onChange={() => {
                        const newAnswers = [...answers];
                        newAnswers[qIdx] = oIdx;
                        setAnswers(newAnswers);
                      }}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <Button onClick={handleSubmit} className="w-full bg-amber-600 hover:bg-amber-700" disabled={answers.includes(-1)}>
            Submit Quiz
          </Button>
        </>
      )}
    </div>
  );
};

// Main Learning Page Component
const LearningPage = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [enrollment, setEnrollment] = useState(null);
  const [course, setCourse] = useState(null);
  const [progressMap, setProgressMap] = useState({});
  const [currentLesson, setCurrentLesson] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedChapters, setExpandedChapters] = useState({});
  const [showNotes, setShowNotes] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);

  useEffect(() => {
    fetchData();
  }, [enrollmentId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get enrollment and progress
      const progressRes = await axios.get(`${API}/lms/progress/${enrollmentId}`);
      setEnrollment(progressRes.data.enrollment);
      setProgressMap(progressRes.data.progress || {});

      // Get full course
      const courseRes = await axios.get(`${API}/lms/courses/${progressRes.data.enrollment.course_id}`);
      setCourse(courseRes.data);

      // Set first lesson or last accessed
      if (courseRes.data.modules?.length > 0) {
        const firstModule = courseRes.data.modules[0];
        setExpandedModules({ [firstModule.id]: true });
        if (firstModule.chapters?.length > 0) {
          const firstChapter = firstModule.chapters[0];
          setExpandedChapters({ [firstChapter.id]: true });
          if (firstChapter.lessons?.length > 0) {
            setCurrentLesson(firstChapter.lessons[0]);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load learning data:', error);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const updateProgress = async (percent, position) => {
    if (!currentLesson) return;
    try {
      await axios.post(`${API}/lms/progress/${enrollmentId}/${currentLesson.id}`, {
        video_watched_percent: percent,
        video_last_position: position,
        time_spent: 10  // 10 seconds per update
      });
    } catch (error) {
      console.error('Failed to update progress');
    }
  };

  const markComplete = async () => {
    if (!currentLesson) return;
    try {
      await axios.post(`${API}/lms/progress/${enrollmentId}/${currentLesson.id}`, {
        is_completed: true,
        video_watched_percent: 100
      });
      toast.success('Lesson marked as complete!');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark complete');
    }
  };

  const submitQuiz = async (answers) => {
    if (!currentLesson) return null;
    try {
      const res = await axios.post(`${API}/lms/quiz/${enrollmentId}/${currentLesson.id}/submit`, { answers });
      fetchData();
      return res.data;
    } catch (error) {
      toast.error('Failed to submit quiz');
      return null;
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isLessonCompleted = (lessonId) => {
    return enrollment?.completed_lessons?.includes(lessonId) || progressMap[lessonId]?.is_completed;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!course || !enrollment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <BookOpen className="h-12 w-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">Course not found</p>
          <Button onClick={() => navigate('/student/dashboard')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" className="text-white" onClick={() => navigate('/student/dashboard')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-white font-bold">{course.title}</h1>
              <p className="text-slate-400 text-sm">{enrollment.progress_percent?.toFixed(0)}% complete</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-slate-700 rounded-full h-2 w-48">
              <div 
                className="bg-emerald-500 h-full rounded-full transition-all"
                style={{ width: `${enrollment.progress_percent || 0}%` }}
              />
            </div>
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-60px)]">
        {/* Sidebar - Course Content */}
        <div className="w-80 bg-slate-800 border-r border-slate-700 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-white font-bold mb-4">Course Content</h2>
            
            {(course.modules || []).map((module, mIdx) => (
              <div key={module.id} className="mb-2">
                <button
                  className="w-full flex items-center justify-between p-3 bg-slate-700 rounded-lg text-white hover:bg-slate-600"
                  onClick={() => setExpandedModules({...expandedModules, [module.id]: !expandedModules[module.id]})}
                >
                  <span className="text-sm font-medium text-left">{mIdx + 1}. {module.title}</span>
                  {expandedModules[module.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                
                {expandedModules[module.id] && (
                  <div className="mt-1 ml-2">
                    {(module.chapters || []).map((chapter, cIdx) => (
                      <div key={chapter.id} className="mb-1">
                        <button
                          className="w-full flex items-center justify-between p-2 text-slate-300 hover:bg-slate-700 rounded text-left"
                          onClick={() => setExpandedChapters({...expandedChapters, [chapter.id]: !expandedChapters[chapter.id]})}
                        >
                          <span className="text-xs">{cIdx + 1}. {chapter.title}</span>
                          {expandedChapters[chapter.id] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        </button>
                        
                        {expandedChapters[chapter.id] && (
                          <div className="ml-4 space-y-1 mt-1">
                            {(chapter.lessons || []).map((lesson, lIdx) => (
                              <button
                                key={lesson.id}
                                className={`w-full flex items-center gap-2 p-2 rounded text-left text-xs ${
                                  currentLesson?.id === lesson.id 
                                    ? 'bg-purple-600 text-white' 
                                    : 'text-slate-400 hover:bg-slate-700'
                                }`}
                                onClick={() => { setCurrentLesson(lesson); setShowNotes(false); setShowQuiz(false); }}
                              >
                                {isLessonCompleted(lesson.id) ? (
                                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                ) : (
                                  <PlayCircle className="h-4 w-4 flex-shrink-0" />
                                )}
                                <span className="flex-1 truncate">{lIdx + 1}. {lesson.title}</span>
                                <span className="text-[10px] text-slate-500">{formatDuration(lesson.video_duration || 0)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto bg-slate-100">
          {currentLesson ? (
            <div>
              {/* Video Player */}
              {!showNotes && !showQuiz && (
                <div className="bg-black">
                  {currentLesson.video_type === 'youtube' ? (
                    <YouTubePlayer
                      videoUrl={currentLesson.video_url}
                      onProgress={updateProgress}
                      onComplete={markComplete}
                      lastPosition={progressMap[currentLesson.id]?.video_last_position}
                    />
                  ) : (
                    <DirectVideoPlayer
                      videoUrl={currentLesson.video_url}
                      onProgress={updateProgress}
                      onComplete={markComplete}
                      lastPosition={progressMap[currentLesson.id]?.video_last_position}
                    />
                  )}
                </div>
              )}

              {/* Lesson Info & Actions */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{currentLesson.title}</h2>
                    {currentLesson.description && (
                      <p className="text-slate-600 mt-1">{currentLesson.description}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {currentLesson.notes_text || currentLesson.notes_pdf_url ? (
                      <Button 
                        variant={showNotes ? "default" : "outline"} 
                        onClick={() => { setShowNotes(!showNotes); setShowQuiz(false); }}
                      >
                        <FileText className="h-4 w-4 mr-2" /> Notes
                      </Button>
                    ) : null}
                    {currentLesson.has_quiz && (
                      <Button 
                        variant={showQuiz ? "default" : "outline"}
                        onClick={() => { setShowQuiz(!showQuiz); setShowNotes(false); }}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                      >
                        <HelpCircle className="h-4 w-4 mr-2" /> Quiz
                      </Button>
                    )}
                    {!isLessonCompleted(currentLesson.id) && (
                      <Button onClick={markComplete} className="bg-emerald-600 hover:bg-emerald-700">
                        <CheckCircle className="h-4 w-4 mr-2" /> Mark Complete
                      </Button>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between text-sm text-slate-500 mb-1">
                    <span>Lesson Progress</span>
                    <span>{(progressMap[currentLesson.id]?.video_watched_percent || 0).toFixed(0)}%</span>
                  </div>
                  <div className="bg-slate-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-full rounded-full transition-all"
                      style={{ width: `${progressMap[currentLesson.id]?.video_watched_percent || 0}%` }}
                    />
                  </div>
                </div>

                {/* Notes Section */}
                {showNotes && (
                  <div className="bg-white rounded-xl border p-6 space-y-4">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" /> Lesson Notes
                    </h3>
                    {currentLesson.notes_text && (
                      <div className="prose max-w-none">
                        <p className="whitespace-pre-wrap">{currentLesson.notes_text}</p>
                      </div>
                    )}
                    {currentLesson.notes_pdf_url && (
                      <a 
                        href={currentLesson.notes_pdf_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <Download className="h-4 w-4" /> Download PDF Notes
                      </a>
                    )}
                  </div>
                )}

                {/* Quiz Section */}
                {showQuiz && currentLesson.has_quiz && (
                  <div className="bg-white rounded-xl border p-6">
                    <QuizComponent
                      quiz={currentLesson.quiz_questions}
                      onSubmit={submitQuiz}
                      existingScore={progressMap[currentLesson.id]?.quiz_score}
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <PlayCircle className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500">Select a lesson to start learning</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LearningPage;
